import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useIdleTimeout } from '../../hooks/useIdleTimeout';
import { useReleaseNotes } from '../../hooks/useReleaseNotes';
import TopBar from './TopBar';
import Sidebar from './Sidebar';
import AccessibilityToolbar from './AccessibilityToolbar';
import ReleaseNotesModal from '../shared/ReleaseNotesModal';

const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export default function AppShell() {
  const { currentUser, authLoading, userProfile, setUserProfile } = useAuth();
  const { tenantLoading } = useTenant();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const autoShownRef = useRef(false);

  const { notes, unseenNotes, loaded } = useReleaseNotes(userProfile);

  const doLogout = useCallback(() => signOut(auth), []);

  useIdleTimeout({ timeoutMs: TIMEOUT_MS, onTimeout: doLogout });

  useEffect(() => {
    if (loaded && unseenNotes.length > 0 && !autoShownRef.current) {
      autoShownRef.current = true;
      setWhatsNewOpen(true);
    }
  }, [loaded, unseenNotes.length]);

  if (authLoading || tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div role="status" aria-live="polite" className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" aria-hidden="true" />
          <p className="text-sm text-gray-700">Loading…</p>
        </div>
      </div>
    );
  }

  if (!currentUser) return <Navigate to="/login" replace />;

  return (
    <div className="flex flex-col h-screen">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[200] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:font-medium focus:shadow-lg"
      >
        Skip to main content
      </a>

      <div id="status-announcer" aria-live="polite" aria-atomic="true" className="sr-only" />

      <TopBar
        onMenuClick={() => setSidebarOpen(true)}
        onWhatsNewClick={() => setWhatsNewOpen(true)}
        unseenCount={unseenNotes.length}
      />

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:flex lg:shrink-0">
          <Sidebar />
        </div>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            aria-hidden="true"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div
          className={`fixed inset-y-0 left-0 z-50 lg:hidden transition-transform duration-200 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <Sidebar onClose={() => setSidebarOpen(false)} />
        </div>

        <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto bg-gray-50 p-6 focus:outline-none">
          <Outlet />
        </main>
      </div>
      <AccessibilityToolbar />

      {whatsNewOpen && (
        <ReleaseNotesModal
          notes={notes}
          unseenNotes={unseenNotes}
          onClose={() => setWhatsNewOpen(false)}
          userProfile={userProfile}
          setUserProfile={setUserProfile}
        />
      )}
    </div>
  );
}
